package co.maytes.standupbot.dashboard.components.widgets

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.motion.rotatesWhenOpen
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideArrowLeft
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideChevronDown
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideScrollText
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideSettings
import co.maytes.standupbot.dashboard.styles.Motion
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Shadow
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.dto.auth.CurrentUserDto
import co.maytes.standupbot.shared.dto.auth.RoleDto
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.ariaLabel
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.onKeyDown
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.role
import com.varabyte.kobweb.compose.ui.modifiers.tabIndex
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px

@Composable
fun UserMenu(
    user: CurrentUserDto,
    isAdmin: Boolean,
    onSettings: () -> Unit,
    onSignOut: () -> Unit,
    onViewOnboarding: (() -> Unit)? = null,
    modifier: Modifier = Modifier,
    sidebar: Boolean = false,
) {
    var open by remember { mutableStateOf(false) }

    Box(
        modifier = Modifier
            .position(Position.Relative)
            .then(modifier),
    ) {
        Row(
            modifier = Modifier
                .cursor(Cursor.Pointer)
                .let { base ->
                    if (sidebar) {
                        base.fillMaxWidth().padding(Spacing.xs)
                    } else {
                        base.padding(topBottom = Spacing.xxs, leftRight = Spacing.xs)
                    }
                }
                .borderRadius(Radius.md)
                .classNames("sb-menu-item")
                .role("button")
                .tabIndex(0)
                .ariaLabel("Account menu")
                .attrsModifier {
                    attr("aria-haspopup", "menu")
                    attr("aria-expanded", open.toString())
                }
                .onClick { open = !open }
                .onKeyDown { event ->
                    if (event.key == "Enter" || event.key == " ") {
                        event.preventDefault()
                        open = !open
                    }
                }
                .styleModifier { property("transition", Motion.TRANSITION_HOVER) },
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(Spacing.xs),
        ) {
            Avatar(
                name = user.displayName,
                src = user.avatarUrl,
                sizePx = if (sidebar) 28 else 32,
                tone = AvatarTone.Neutral,
            )
            if (sidebar) {
                Column(modifier = Modifier.styleModifier { property("min-width", "0"); property("flex", "1 1 auto") }) {
                    SpanText(
                        text = user.displayName,
                        modifier = Modifier
                            .fontSize(Typography.body.size)
                            .fontWeight(500)
                            .color(Palette.textPrimary)
                            .styleModifier {
                                property("overflow", "hidden")
                                property("text-overflow", "ellipsis")
                                property("white-space", "nowrap")
                            },
                    )
                    SpanText(
                        text = roleLabel(user, isAdmin),
                        modifier = Modifier
                            .fontSize(Typography.caption.size)
                            .color(Palette.textTertiary),
                    )
                }
            }
            Box(
                modifier = Modifier
                    .color(Palette.textSecondary)
                    .rotatesWhenOpen(open),
            ) {
                LucideChevronDown(size = 16)
            }
        }

        Popover(
            open = open,
            placement = if (sidebar) PopoverPlacement.TopStart else PopoverPlacement.BottomEnd,
            onDismiss = { open = false },
            role = "menu",
            ariaLabel = "Account menu",
            modifier = Modifier
                .classNames("sb-surface")
                .borderRadius(Radius.lg)
                .styleModifier {
                    property("border", "1px solid")
                    property("box-shadow", Shadow.LG)
                    property("overflow", "hidden")
                    property("width", "max-content")
                    property("min-width", "16.5rem")
                    property("max-width", "min(24rem, calc(100vw - 1rem))")
                },
        ) {
            Column(modifier = Modifier.fillMaxWidth()) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(Spacing.md),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(Spacing.sm),
                ) {
                    Avatar(
                        name = user.displayName,
                        src = user.avatarUrl,
                        sizePx = 38,
                        tone = AvatarTone.Neutral,
                    )
                    Column(modifier = Modifier.styleModifier { property("min-width", "0"); property("flex", "1 1 auto") }) {
                        Row(
                            modifier = Modifier.styleModifier { property("flex-wrap", "wrap") },
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(Spacing.xs),
                        ) {
                            SpanText(
                                text = user.displayName,
                                modifier = Modifier
                                    .fontSize(Typography.body.size)
                                    .fontWeight(600)
                                    .color(Palette.textPrimary),
                            )
                            if (isAdmin) RoleBadge(roleLabel(user, isAdmin))
                        }
                        user.email?.let { email ->
                            SpanText(
                                text = email,
                                modifier = Modifier
                                    .margin(top = 2.px)
                                    .fontSize(Typography.caption.size)
                                    .color(Palette.textSecondary)
                                    .styleModifier {
                                        property("overflow-wrap", "anywhere")
                                    },
                            )
                        }
                    }
                }
                if (isAdmin || onViewOnboarding != null) {
                    Divider()
                    Column(modifier = Modifier.fillMaxWidth().padding(Spacing.xs)) {
                        if (isAdmin) {
                            MenuItem(label = "Settings", onClick = {
                                open = false
                                onSettings()
                            }) { LucideSettings(size = 16, strokeColor = it) }
                        }
                        if (onViewOnboarding != null) {
                            MenuItem(label = "Onboarding tour", onClick = {
                                open = false
                                onViewOnboarding()
                            }) { LucideScrollText(size = 16, strokeColor = it) }
                        }
                    }
                }
                Divider()
                Column(modifier = Modifier.fillMaxWidth().padding(Spacing.xs)) {
                    MenuItem(label = "Sign out", onClick = {
                        open = false
                        onSignOut()
                    }, danger = true) { LucideArrowLeft(size = 16, strokeColor = it) }
                }
            }
        }
    }
}

@Composable
private fun Divider() {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(1.px)
            .backgroundColor(Palette.borderSubtle),
    )
}

@Composable
private fun RoleBadge(label: String) {
    SpanText(
        text = label,
        modifier = Modifier
            .color(Palette.accent)
            .fontSize(10.px)
            .fontWeight(600)
            .styleModifier {
                property("background-color", Palette.accentSubtle.cssRgb())
                property("border-radius", "5px")
                property("padding", "1px 6px")
                property("letter-spacing", "0.04em")
                property("text-transform", "none")
                property("line-height", "1.4")
            },
    )
}

@Composable
private fun MenuItem(
    label: String,
    onClick: () -> Unit,
    danger: Boolean = false,
    icon: (@Composable (String) -> Unit)? = null,
) {
    val tone = if (danger) Palette.error else Palette.textPrimary
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .borderRadius(Radius.md)
            .padding(topBottom = Spacing.xs, leftRight = Spacing.sm)
            .cursor(Cursor.Pointer)
            .color(tone)
            .classNames("sb-menu-item")
            .role("menuitem")
            .tabIndex(0)
            .onClick { onClick() }
            .onKeyDown { event ->
                if (event.key == "Enter" || event.key == " ") {
                    event.preventDefault()
                    onClick()
                }
            }
            .styleModifier { property("transition", Motion.TRANSITION_HOVER) },
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(Spacing.sm),
    ) {
        if (icon != null) {
            Box(
                modifier = Modifier
                    .styleModifier {
                        property("display", "inline-flex")
                        property("align-items", "center")
                        property("justify-content", "center")
                        property("flex-shrink", "0")
                        property("color", tone.cssRgb())
                    },
            ) {
                icon("currentColor")
            }
        }
        SpanText(
            text = label,
            modifier = Modifier
                .fontSize(Typography.body.size)
                .fontWeight(500),
        )
    }
}


private fun roleLabel(user: CurrentUserDto, isAdmin: Boolean): String = when (user.role) {
    RoleDto.OWNER -> "Owner"
    RoleDto.ADMIN -> "Admin"
    RoleDto.MEMBER -> "Member"
    RoleDto.VIEWER -> if (isAdmin) "Admin" else "Viewer"
}
